import React from "react";
import { COLORS } from "./tokens";

/**
 * THE GOLDEN PATH — sixth core logo.
 * A serene landscape scene: a winding golden path leads into the distance
 * toward a soft sun on the horizon, framed by minimal tree silhouettes.
 * Tagline (used with this mark): "There is healing in movement."
 * Product tagline (with this mark): "Walk · Hydrate · Thrive."
 */
const GoldenPathLogo = React.forwardRef(function GoldenPathLogo(
  {
    size = 480,
    withFrame = true,
    showTagline = true,
    id = "rona-golden-path",
  },
  ref
) {
  const ratio = 500 / 800;
  return (
    <svg
      ref={ref}
      id={id}
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 800 500"
      width={size}
      height={size * ratio}
      role="img"
      aria-label="The Golden Path — There is healing in movement"
    >
      <defs>
        <linearGradient id="gp-sky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={COLORS.dustyBlue} stopOpacity="0.15" />
          <stop offset="55%" stopColor={COLORS.yellowSoft} stopOpacity="0.7" />
          <stop offset="100%" stopColor={COLORS.sandBeige} stopOpacity="0.5" />
        </linearGradient>
        <linearGradient id="gp-path-fill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={COLORS.goldLight} />
          <stop offset="70%" stopColor={COLORS.gold} />
          <stop offset="100%" stopColor={COLORS.goldDeep} />
        </linearGradient>
        <radialGradient id="gp-sun-glow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={COLORS.yellowSoft} stopOpacity="0.95" />
          <stop offset="60%" stopColor={COLORS.yellow} stopOpacity="0.4" />
          <stop offset="100%" stopColor={COLORS.yellow} stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* Sky */}
      <rect width="800" height="500" fill="url(#gp-sky)" />

      {/* Sun glow behind horizon */}
      <circle cx="400" cy="300" r="120" fill="url(#gp-sun-glow)" />
      <circle cx="400" cy="300" r="26" fill={COLORS.yellowSoft} opacity="0.9" />

      {/* Horizon line — very subtle */}
      <line
        x1="60"
        y1="310"
        x2="740"
        y2="310"
        stroke={COLORS.goldDeep}
        strokeOpacity="0.25"
        strokeWidth="0.8"
      />

      {/* Distant tree silhouettes — left cluster */}
      <g fill={COLORS.sageDeep} opacity="0.55">
        <path d="M 90 310 C 82 285, 88 258, 100 240 C 112 258, 118 285, 110 310 Z" />
        <path d="M 130 310 C 124 288, 130 268, 140 254 C 150 268, 156 288, 150 310 Z" />
        <path d="M 60 310 C 55 292, 62 275, 72 262 C 82 275, 88 292, 82 310 Z" />
        <path d="M 165 310 C 161 296, 166 282, 173 272 C 180 282, 185 296, 181 310 Z" />
      </g>

      {/* Distant tree silhouettes — right cluster */}
      <g fill={COLORS.sageDeep} opacity="0.55">
        <path d="M 690 310 C 682 285, 688 258, 700 240 C 712 258, 718 285, 710 310 Z" />
        <path d="M 730 310 C 724 288, 730 268, 740 254 C 750 268, 756 288, 750 310 Z" />
        <path d="M 660 310 C 655 292, 662 275, 672 262 C 682 275, 688 292, 682 310 Z" />
        <path d="M 625 310 C 621 296, 626 282, 633 272 C 640 282, 645 296, 641 310 Z" />
      </g>

      {/* Small foreground grass suggestions */}
      <g stroke={COLORS.sageDeep} strokeWidth="1.2" strokeLinecap="round" opacity="0.5">
        <line x1="220" y1="470" x2="220" y2="455" />
        <line x1="240" y1="475" x2="240" y2="458" />
        <line x1="580" y1="470" x2="580" y2="455" />
        <line x1="560" y1="475" x2="560" y2="458" />
        <line x1="200" y1="410" x2="200" y2="400" />
        <line x1="600" y1="410" x2="600" y2="400" />
      </g>

      {/* THE GOLDEN PATH — stepping stones receding toward the horizon */}
      <g fill="url(#gp-path-fill)">
        <circle cx="400" cy="482" r="22" />
        <circle cx="386" cy="446" r="18" />
        <circle cx="414" cy="412" r="15" />
        <circle cx="393" cy="382" r="12" />
        <circle cx="410" cy="356" r="9.5" />
        <circle cx="398" cy="336" r="7" />
        <circle cx="401" cy="322" r="4.5" />
      </g>
      {/* Subtle glow beneath each stone */}
      <g fill={COLORS.yellowSoft} opacity="0.35">
        <circle cx="400" cy="482" r="30" />
        <circle cx="386" cy="446" r="24" />
        <circle cx="414" cy="412" r="19" />
        <circle cx="393" cy="382" r="15" />
        <circle cx="410" cy="356" r="12" />
      </g>

      {/* Tagline (optional) */}
      {showTagline && (
        <>
          <text
            x="400"
            y="60"
            textAnchor="middle"
            fontFamily="'Marcellus', serif"
            fontSize="14"
            letterSpacing="8"
            fill={COLORS.goldDeep}
          >
            THE GOLDEN PATH
          </text>
          <text
            x="400"
            y="110"
            textAnchor="middle"
            fontFamily="'Cormorant Garamond', serif"
            fontSize="30"
            fontStyle="italic"
            fill={COLORS.brownDeep}
          >
            There is healing in movement.
          </text>
          <text
            x="400"
            y="145"
            textAnchor="middle"
            fontFamily="'Marcellus', serif"
            fontSize="11"
            letterSpacing="14"
            fill={COLORS.gold}
          >
            WALK  ·  HYDRATE  ·  THRIVE
          </text>
        </>
      )}

      {/* Frame */}
      {withFrame && (
        <rect
          x="10"
          y="10"
          width="780"
          height="480"
          fill="none"
          stroke={COLORS.gold}
          strokeOpacity="0.55"
          strokeWidth="1"
        />
      )}
    </svg>
  );
});

export default GoldenPathLogo;
